import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Headphones, Monitor, Download, MousePointer, Shield, 
  CheckCircle, Wifi, Clock, ChevronRight, MessageCircle
} from "lucide-react";
import MainLayout from "../components/MainLayout";
import SEO, { pageSEO } from "../components/SEO";

const WHATSAPP_NUMBER = "447435324947";

const RemoteSupportPage = () => {
  const steps = [
    {
      number: "1",
      title: "Download AnyViewer",
      description: "Click the download button below to get the free AnyViewer software.",
      icon: Download
    },
    {
      number: "2",
      title: "Install & Open",
      description: "Run the installer and open AnyViewer on your computer.",
      icon: Monitor
    },
    {
      number: "3",
      title: "Share Your Device ID",
      description: "Find your Device ID in AnyViewer and share it with us via phone or WhatsApp.",
      icon: Shield
    },
    {
      number: "4",
      title: "We Connect & Help",
      description: "Accept the connection request and we'll fix your issue remotely!",
      icon: Wifi
    }
  ];

  return (
    <MainLayout>
      <SEO {...pageSEO.remoteSupport} />
      
      {/* Hero Section */}
      <section className="pt-12 pb-16 gradient-hero gradient-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(59,130,246,0.4)]">
                <Headphones className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Remote Support
            </h1>
            <p className="text-xl text-slate-300 mb-4">
              Get instant help without leaving your home!
            </p>
            <p className="text-lg text-slate-400">
              We can connect to your computer securely and fix issues in real-time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-medium mb-6">
              <CheckCircle className="w-4 h-4" />
              Free & Secure Download
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Step 1: Download AnyViewer
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              AnyViewer is a safe and trusted remote desktop software. Download it to get started.
            </p>

            <a
              href="https://www.anyviewer.com/download.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white text-xl font-semibold px-10 py-5 rounded-xl shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] transition-all"
              data-testid="download-anyviewer-btn"
            >
              <Download className="w-7 h-7" />
              Download AnyViewer
            </a>

            <p className="text-slate-500 text-sm mt-4">
              Works on Windows 11, 10, 8, 7 and Windows Server
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 text-lg">
              Follow these simple steps to get remote support
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="card text-center p-6 h-full">
                  <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-blue-500 font-bold text-xl">{step.number}</span>
                  </div>
                  <div className="w-14 h-14 bg-slate-800 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-7 h-7 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-sm">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ChevronRight className="w-6 h-6 text-slate-600" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card p-8 md:p-12 text-center border-blue-500/20"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready for Remote Support?
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              After downloading AnyViewer, contact us with your <span className="text-blue-400 font-semibold">Device ID</span> and we'll connect right away!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi! I need remote support. My AnyViewer Device ID is: `}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold px-8 py-4 rounded-xl transition-all"
                data-testid="whatsapp-support-btn"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp Us
              </a>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold px-8 py-4 rounded-xl transition-all"
                data-testid="message-support-btn"
              >
                <MessageCircle className="w-6 h-6" />
                Message on Site
              </Link>
            </div>

            <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <p className="text-blue-400 text-sm">
                <strong>Tip:</strong> Have your Device ID ready when you contact us. You can find it on the main screen of AnyViewer after installation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security Note */}
      <section className="py-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 justify-center text-center">
            <Shield className="w-8 h-8 text-green-500" />
            <p className="text-slate-400">
              <span className="text-green-400 font-semibold">100% Secure:</span> You control the connection. We can only access your computer when you accept the request, and you can end the session anytime.
            </p>
          </div>
        </div>
      </section>
    </MainLayout>
  );
};


export default RemoteSupportPage;
